import React, { createContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { reportError, setReporterUser } from '../../services/errorReporter';

export const AuthContext = createContext(null);

async function fetchUserProfile(userId) {
  const { data, error } = await supabase
    .from('users')
    .select(`
      name, position, status, role_id,
      roles ( name, role_permissions ( permissions ( code ) ) )
    `)
    .eq('id', userId)
    .single();

  if (error || !data) {
    reportError(error ?? new Error('fetchUserProfile returned no data'), { userId });
    return null;
  }
  const permissions = data.roles?.role_permissions
    ?.map((rp) => rp.permissions?.code)
    .filter(Boolean) ?? [];
  return {
    name: data.name,
    position: data.position,
    status: data.status,
    role: data.roles?.name ?? null,
    permissions,
  };
}

export async function resolveAccountStatus() {
  try {
    const { data } = await supabase.rpc('my_registration_status');
    return data === 'pending' || data === 'rejected' ? data : 'unknown';
  } catch {
    return 'unknown';
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountStatus, setAccountStatus] = useState(null);

  useEffect(() => {
    // Single source of truth: onAuthStateChange emits INITIAL_SESSION on mount,
    // so we no longer also call getSession() (that dual path caused an init race).
    // activeUserId lets us drop stale profile fetches when the auth user changes.
    // loadedUserId is the user whose profile is currently loaded: supabase fires
    // same-user events (TOKEN_REFRESHED) when a backgrounded tab regains focus,
    // and re-entering `loading` for those unmounts the whole app via
    // ProtectedRoute — destroying any in-progress form state.
    let activeUserId = null;
    let loadedUserId = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const sessionUser = session?.user ?? null;

      if (!sessionUser) {
        activeUserId = null;
        loadedUserId = null;
        setUser(null);
        setUserRole(null);
        setError('');
        setAccountStatus(null);
        setLoading(false);
        setReporterUser(null);
        return;
      }

      // Same signed-in user with a profile already loaded: keep the refreshed
      // session and revalidate the profile SILENTLY — never flip `loading`
      // (that unmounts the app via ProtectedRoute and destroys form state).
      // The revalidation matters: the client-side status check below is the
      // app's only deactivation gate (RLS does not check users.status), so a
      // deactivated account must still get caught on the next token refresh.
      if (sessionUser.id === loadedUserId) {
        activeUserId = sessionUser.id;
        setUser(sessionUser);
        setReporterUser(sessionUser);
        setTimeout(async () => {
          const profile = await fetchUserProfile(sessionUser.id);
          if (activeUserId !== sessionUser.id) return; // superseded by a newer auth event
          if (!profile) return; // transient failure: keep current state, retry on next event
          if (profile.status === 'inactive') {
            loadedUserId = null;
            setError('Your account has been deactivated. Contact an admin for assistance.');
            setUser(null);
            setUserRole(null);
            return;
          }
          setUserRole(profile);
          setAccountStatus('active');
        }, 0);
        return;
      }

      activeUserId = sessionUser.id;
      loadedUserId = null;
      setUser(sessionUser);
      setReporterUser(sessionUser);
      setLoading(true);

      // Defer DB work out of the auth callback to avoid supabase-js lock contention.
      setTimeout(async () => {
        const profile = await fetchUserProfile(sessionUser.id);
        if (activeUserId !== sessionUser.id) return; // superseded by a newer auth event

        if (!profile) {
          const status = await resolveAccountStatus();
          if (activeUserId !== sessionUser.id) return; // superseded by a newer auth event
          setUserRole(null);
          setAccountStatus(status);
          setLoading(false);
          return;
        }
        if (profile.status === 'inactive') {
          setError('Your account has been deactivated. Contact an admin for assistance.');
          setUser(null);
          setUserRole(null);
          setLoading(false);
          return;
        }
        setError('');
        setUserRole(profile);
        setAccountStatus('active');
        setLoading(false);
        // Only a fully-loaded profile arms the same-user skip: pending/rejected
        // and failed loads stay retryable on the next auth event.
        loadedUserId = sessionUser.id;
      }, 0);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const value = useMemo(
    () => ({ user, userRole, loading, error, accountStatus }),
    [user, userRole, loading, error, accountStatus]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
